import { Injectable } from '@angular/core';
import { filter, fromEvent, map, Observable, share } from 'rxjs';
import { debounceTime } from 'rxjs/operators';


export interface ClickTracker {
  elementName: string;
  ltv: string;
  elementText: string;
  icon: string;
  pagePath: string;
  href: string;
}


@Injectable({ providedIn: 'root' })
export class ClickTrackerService {

  /** Emits only clicks on <button> or <a> */
  readonly clicks$: Observable<ClickTracker>;

  constructor() {

    this.clicks$ = fromEvent<MouseEvent>(
      document,
      'click',
      { capture: true, passive: true } as AddEventListenerOptions
    ).pipe(
      map(event => {
        const el = (event.target as HTMLElement)?.closest(
          'button, a, .custom-radio, .checkbox-item, .click-capture'
        ) as HTMLElement | null;

        const pathAtClick = window.location.pathname;

        return { event, el, pathAtClick };
      }),

      filter(({ el }) => !!el),

      debounceTime(150),

      map(({ el, pathAtClick }) => {
        const isRadio = el!.classList.contains('custom-radio');
        const isCheckbox = el!.classList.contains('checkbox-item');
        const isClickCap = el!.classList.contains('click-capture');
        const isSelfLtv = el!.classList.contains('self-ltv');

        let realEl: HTMLElement = el!;
        if (isRadio) {
          const input = el!.querySelector('input[type="radio"]') as HTMLElement | null;
          if (input) realEl = input;
        }

        /** ---------- LTV ---------- */
        let ltv: string | null = null;
        if (isClickCap && isSelfLtv) {
          const iAny = el!.querySelector('i');
          ltv = iAny?.textContent?.trim()
            ?? el!.getAttribute('data-ltv')?.trim()
            ?? (el as any).dataset?.ltv
            ?? null;
        } else {
          const iTag = el!.querySelector('i.ltv');
          ltv = iTag?.textContent?.trim() ?? null;
        }

        /** ---------- mat-icon ---------- */
        const matIcon = el!.querySelector('mat-icon');
        const icon = matIcon?.textContent?.trim() ?? null;

        /** ---------- TEXT (data-label prioridade) ---------- */
        let text: string | null =
          el!.getAttribute('data-label')?.trim()
          ?? (el as any).dataset?.label
          ?? null;

        if (!text) {
          if (isClickCap && isSelfLtv) {
            const cloned = el!.cloneNode(true) as HTMLElement;
            cloned.querySelectorAll('i, mat-icon').forEach(n => n.remove());
            text = cloned.textContent?.trim() ?? null;
          } else if (isClickCap) {
            const cloned = el!.cloneNode(true) as HTMLElement;
            cloned.querySelectorAll('i.ltv, mat-icon').forEach(n => n.remove());
            text = cloned.textContent?.trim() ?? null;
          } else if (isRadio || isCheckbox) {
            const spanLabel = el!.querySelector('.label');
            text = spanLabel?.textContent?.trim() ?? null;
          } else {
            const cloned = el!.cloneNode(true) as HTMLElement;
            cloned.querySelectorAll('i.ltv, mat-icon').forEach(n => n.remove());
            text = cloned.textContent?.trim() ?? null;
          }
        }

        /** ---------- HREF ---------- */
        const ownHref = realEl.getAttribute?.('href') ?? el!.getAttribute?.('href') ?? null;
        const innerLink = el!.querySelector('a[href]') as HTMLAnchorElement | null;
        const href = ownHref ?? innerLink?.getAttribute('href') ?? null;

        /** ---------- TAG ---------- */
        const tag = isCheckbox ? 'INPUT' : realEl.tagName;

        /** ---------- PARENT LABEL TEXT ---------- */
          // procura label ascendente
        let parentLabelText: string | null =
            this.getNearestLabelText(realEl) ??
            this.getNearestLabelText(el!) ??
            null;


        /** ---------- normalização: se igual ao próprio texto → NULL ---------- */
        if (parentLabelText && text && parentLabelText === text) {
          parentLabelText = null;
        }


        return {
          elementName: tag,
          ltv,
          elementText: text,
          icon,
          pagePath: pathAtClick,
          href,
          parentLabelText
        };
      }),

      share()
    );
  }


  getNearestLabelText(startEl: HTMLElement, maxDepth = 5): string | null {
    // 1) prioridades: títulos primeiro (h1 > ... > h6), depois rótulos/semânticos
    const TITLE_TAGS = ['h1','h2','h3','h4','h5','h6'];
    const LABEL_TAGS = ['label','mat-label','span.label','strong','p','span','div'];
    const STRIP_SELECTOR = 'i, mat-icon, svg, button, [aria-hidden="true"]';

    const cleanTextFromEl = (el: Element | null): string | null => {
      if (!el) return null;
      const cloned = el.cloneNode(true) as HTMLElement;
      cloned.querySelectorAll(STRIP_SELECTOR).forEach(n => n.remove());
      const t = (cloned.textContent || '').replace(/\s+/g, ' ').trim();
      return t.length ? t : null;
    };

    const attrLabel = (el: Element | null): string | null => {
      if (!el) return null;
      const lbl =
        (el as HTMLElement).getAttribute?.('data-label')?.trim()
        ?? (el as any).dataset?.label
        ?? el.getAttribute?.('aria-label')
        ?? el.getAttribute?.('title')
        ?? null;
      return lbl && lbl.trim() ? lbl.trim() : null;
    };

    // --- 0) atributos no próprio elemento
    const selfAttr = attrLabel(startEl);
    if (selfAttr) return selfAttr;

    // --- 0.1) aria-labelledby
    const ariaLblBy = startEl.getAttribute('aria-labelledby');
    if (ariaLblBy) {
      for (const id of ariaLblBy.split(/\s+/)) {
        const ref = id && document.getElementById(id);
        const txt = cleanTextFromEl(ref as Element);
        if (txt) return txt;
      }
    }

    // --- 1) label[for] vinculado (se for input com id)
    if ((startEl as HTMLInputElement).id) {
      const byFor = document.querySelector(`label[for="${(startEl as HTMLInputElement).id}"]`);
      const byForText = cleanTextFromEl(byFor as Element);
      if (byForText) return byForText;
    }

    // --- 2) label ancestral
    const enclosingLabel = startEl.closest('label');
    const enclosingText = cleanTextFromEl(enclosingLabel as Element);
    if (enclosingText) return enclosingText;

    // --- helpers
    const scoreTag = (el: Element): number => {
      const tag = el.tagName.toLowerCase();
      if (TITLE_TAGS.includes(tag)) {
        // h1=100, h2=95, ... h6=75 (exemplo)
        const level = Number(tag[1]);
        return 100 - (level - 1) * 5;
      }
      // prioridades decrescentes para labels genéricos
      const idxLabel = LABEL_TAGS.findIndex(sel => sel.split('.')[0] === tag || el.matches(sel));
      // base 60 e caindo
      return idxLabel >= 0 ? 60 - idxLabel * 2 : 0;
    };

    const bestTextFrom = (candidates: Element[], distancePenalty = 0): string | null => {
      // ordena por (scoreTag desc)
      const sorted = candidates
        .map(el => ({ el, score: scoreTag(el) - distancePenalty }))
        .sort((a, b) => b.score - a.score);

      for (const { el } of sorted) {
        const t = cleanTextFromEl(el);
        if (t) return t;
        // tenta um descendente preferido
        const innerTitle = el.querySelector(TITLE_TAGS.join(','));
        const innerTitleTxt = cleanTextFromEl(innerTitle as Element);
        if (innerTitleTxt) return innerTitleTxt;

        const innerLabel = el.querySelector(LABEL_TAGS.join(','));
        const innerLabelTxt = cleanTextFromEl(innerLabel as Element);
        if (innerLabelTxt) return innerLabelTxt;
      }
      return null;
    };

    const collectSiblingCandidates = (el: Element): Element[] => {
      const prev: Element[] = [];
      let p: Element | null = el.previousElementSibling;
      while (p) { prev.push(p); p = p.previousElementSibling; }

      const next: Element[] = [];
      let n: Element | null = el.nextElementSibling;
      while (n) { next.push(n); n = n.nextElementSibling; }

      // títulos anteriores têm prioridade máxima, depois labels anteriores, depois posteriores
      const prevTitles = prev.filter(e => TITLE_TAGS.includes(e.tagName.toLowerCase()));
      const prevLabels = prev.filter(e => !prevTitles.includes(e) && (LABEL_TAGS.some(sel => e.matches(sel))));

      const nextTitles = next.filter(e => TITLE_TAGS.includes(e.tagName.toLowerCase()));
      const nextLabels = next.filter(e => !nextTitles.includes(e) && (LABEL_TAGS.some(sel => e.matches(sel))));

      return [...prevTitles, ...prevLabels, ...nextTitles, ...nextLabels];
    };

    // --- 3) subir ancestrais, priorizando títulos em cada nível
    let depth = 0;
    let cursor: Element | null = startEl;
    while (cursor && depth <= maxDepth) {
      // 3.1) atributos no ancestral
      const a = attrLabel(cursor);
      if (a) return a;

      // 3.2) irmãos do cursor (títulos antes)
      const sibs = collectSiblingCandidates(cursor);
      const sibTxt = bestTextFrom(sibs, /*distancePenalty*/ depth * 3);
      if (sibTxt) return sibTxt;

      // 3.3) dentro do próprio ancestral: títulos > labels
      const titlesIn = Array.from(cursor.querySelectorAll(TITLE_TAGS.join(',')));
      const titleTxt = bestTextFrom(titlesIn as Element[], depth * 5);
      if (titleTxt) return titleTxt;

      const labelsIn = Array.from(cursor.querySelectorAll(LABEL_TAGS.join(',')));
      const labelTxt = bestTextFrom(labelsIn as Element[], depth * 5 + 2);
      if (labelTxt) return labelTxt;

      cursor = cursor.parentElement;
      depth++;
    }

    return null;
  }
}
